import moment from 'moment-timezone'
import { AdmissionType, AdmissionTypeValues } from '@/types/general.types'
type MomentOrUndefined = moment.Moment | undefined

type SalesWindowResult = {
  categoryOnSale: boolean
  salesWindowData: Transformed.SalesWindow | undefined
  timeslotEndOffsetMoment: MomentOrUndefined
  startOffsetMoment: MomentOrUndefined
  endOffsetMoment: MomentOrUndefined
}

/**
 * Retrieves sales window data for a specific category and timeslot.
 */
export const getSalesWindowData = (
  category: Transformed.TicketCategory,
  timeslotId: string,
  timeslotEndTimeWithOffset: Nullable<string>,
): SalesWindowResult => {
  const salesWindowData = category.salesWindow.find((el) => el?.id === timeslotId) ?? undefined
  const categoryOnSale = salesWindowData ? salesWindowData.active : true

  const startOffsetMoment = salesWindowData?.startOffset ? moment(salesWindowData.startOffset) : undefined
  const endOffsetMoment = salesWindowData?.endOffset ? moment(salesWindowData.endOffset) : undefined
  const timeslotEndOffsetMoment = timeslotEndTimeWithOffset
    ? moment(timeslotEndTimeWithOffset)
    : undefined

  return { categoryOnSale, salesWindowData, timeslotEndOffsetMoment, startOffsetMoment, endOffsetMoment }
}

/**
 * Determines if a category should be hidden based on sales window state.
 */
export const isCategoryHidden = (
  categoryOnSale: boolean,
  salesWindowData: Transformed.SalesWindow | undefined,
  timeslotEndOffsetMoment: MomentOrUndefined,
): boolean => (
  (!categoryOnSale && !!salesWindowData?.isDOS)
  || !!salesWindowData?.elapsed
  || !!timeslotEndOffsetMoment?.isBefore(moment.now())
)

export const isValidUrl = (url: string): boolean => {
  try {
    return !!new URL(url)
  } catch {
    return false
  }
}

type PortalPassAudience = {
  user: Nullable<Transformed.LoggedInUser>
  portalPassDayPassEnabled: boolean
  admissionType: AdmissionTypeValues
  portalPassUrl: Nullable<string>
}

/**
 * Plain-language explanation of why the Portal Pass URL is unusable, or null when it is fine.
 */
const getPortalPassUrlReason = (portalPassUrl: Nullable<string>): string | null => {
  if (!portalPassUrl) return 'there is no Portal Pass URL to send them to'
  if (!isValidUrl(portalPassUrl)) return `the Portal Pass URL "${portalPassUrl}" is not a valid URL`
  return null
}

/**
 * Every reason this user/seller combination should not be pitched the Portal Pass —
 * empty when they are eligible.
 *
 * @param {PortalPassAudience} args - The user, seller day-pass flag, admission type and destination URL.
 * @returns {string[]} Plain-language reasons, in the order they are checked.
 */
export const getPortalPassIneligibilityReasons = ({
  user,
  portalPassDayPassEnabled,
  admissionType,
  portalPassUrl,
}: PortalPassAudience): string[] => {
  const isLoggedIn = !!user?.profile
  const membershipRoles = user?.rolesAndMembership?.membershipRoles
  const hasMembership = !!membershipRoles?.length
  const loggedInWithMembership = isLoggedIn && hasMembership
  const isExcludedDayPass = !portalPassDayPassEnabled && admissionType === AdmissionType.DayPass

  return [
    loggedInWithMembership ? 'they are signed in and already have a membership' : null,
    isExcludedDayPass ? 'this seller excludes day passes from the promo' : null,
    getPortalPassUrlReason(portalPassUrl),
  ].filter((reason) => reason !== null)
}

/**
 * Whether this user/seller combination should be pitched the Portal Pass at all.
 *
 * Shared by both arms of the Portal Pass interrupt A/B test so the modal and the
 * banner always target exactly the same audience — members already have the pass,
 * sellers can exclude day passes, and there is nowhere to send the user without a
 * valid destination URL.
 *
 * @param {PortalPassAudience} args - The user, seller day-pass flag, admission type and destination URL.
 * @returns {boolean} True when the Portal Pass promo is eligible to display.
 */
export const isPortalPassPromoEligible = (args: PortalPassAudience): boolean => (
  getPortalPassIneligibilityReasons(args).length === 0
)

/**
 * The eligibility verdict as a sentence, so the A/B test audience can be debugged from
 * the browser console without stepping through the conditions by hand.
 *
 * Kept separate from the predicate itself: deciding is pure and happens on every render,
 * while saying so out loud is a side effect and belongs in an effect.
 *
 * @param {string[]} reasons - Reasons from {@link getPortalPassIneligibilityReasons}.
 * @returns {string} The verdict, ready to log.
 */
export const getPortalPassEligibilityMessage = (reasons: string[]): string => (
  reasons.length === 0
    ? '[Portal Pass] This user is eligible to be shown the Portal Pass promo.'
    : `[Portal Pass] This user is not eligible to be shown the Portal Pass promo because ${reasons.join(', and ')}.`
)

export const getDisplayPortalPassModal = ({
  user,
  portalPassDayPassEnabled,
  admissionType,
  interruptPurchaseFlow,
  recentlyDismissed,
  portalPassUrl,
}: PortalPassAudience & {
  interruptPurchaseFlow: boolean | undefined
  recentlyDismissed: boolean
}): boolean => (
  !!interruptPurchaseFlow
  && !recentlyDismissed
  && isPortalPassPromoEligible({ user, portalPassDayPassEnabled, admissionType, portalPassUrl })
)
